import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ConsultingService {
  icon: string;
  title: string;
  color: string;
  image: string;
  desc: string;
  benefits: string[];
  forWho: string;
}

const ServiceBlock = ({ service, index, consultingUrl }: { service: ConsultingService; index: number; consultingUrl: string }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const isEven = index % 2 === 0;

  return (
    <div
      ref={ref}
      className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-10 items-center transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      {/* Image */}
      <div className="w-full lg:w-[45%] flex-shrink-0">
        <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-60 md:h-72 object-cover object-top group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030006]/70 via-transparent to-transparent" />
          <div
            className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold"
            style={{ background: `${service.color}25`, border: `1px solid ${service.color}40`, color: service.color }}
          >
            <i className={`${service.icon} text-sm`} />
            {service.title}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1">
        <div
          className="w-12 h-12 flex items-center justify-center rounded-xl mb-4"
          style={{ background: `${service.color}15`, border: `1px solid ${service.color}30` }}
        >
          <i className={`${service.icon} text-xl`} style={{ color: service.color }} />
        </div>

        <h2 className="text-xl md:text-2xl font-black text-white mb-3">{service.title}</h2>
        <p className="text-white/55 text-sm leading-relaxed mb-5">{service.desc}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
          {service.benefits.map((b) => (
            <div key={b} className="flex items-start gap-2">
              <div
                className="w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                style={{ background: `${service.color}20` }}
              >
                <i className="ri-check-line text-[10px]" style={{ color: service.color }} />
              </div>
              <span className="text-white/55 text-xs leading-relaxed">{b}</span>
            </div>
          ))}
        </div>

        <div className="bg-white/3 border border-white/8 rounded-xl p-3.5 mb-5">
          <span className="text-white/40 text-xs uppercase tracking-wider font-bold">{t('consulting.services.common.forWho')}: </span>
          <span className="text-white/55 text-xs leading-relaxed">{service.forWho}</span>
        </div>

        {consultingUrl.startsWith('http') ? (
          <a
            href={consultingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer text-white hover:opacity-90 hover:scale-105 text-sm"
            style={{ background: `linear-gradient(135deg, ${service.color}, ${service.color}80)` }}
          >
            <i className="ri-calendar-line" />
            {t('consulting.services.common.book')}
          </a>
        ) : (
          <Link
            to={consultingUrl}
            className="whitespace-nowrap inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer text-white hover:opacity-90 hover:scale-105 text-sm"
            style={{ background: `linear-gradient(135deg, ${service.color}, ${service.color}80)` }}
          >
            <i className="ri-calendar-line" />
            {t('consulting.services.common.book')}
          </Link>
        )}
      </div>
    </div>
  );
};

const ConsultingServices = ({ consultingUrl }: { consultingUrl: string }) => {
  const { t } = useTranslation();

  const consultingServices: ConsultingService[] = [
    {
      icon: 'ri-building-2-line',
      title: t('consulting.services.items.llc.title'),
      color: '#8c52ff',
      image: 'https://readdy.ai/api/search-image?query=LLC%20business%20formation%20legal%20documents%20signing%20pen%20dark%20elegant%20background%20purple%20neon%20glow%20professional%20entrepreneur%20startup%20company%20registration%20official%20paperwork&width=700&height=450&seq=llc1&orientation=landscape',
      desc: t('consulting.services.items.llc.desc'),
      benefits: t('consulting.services.items.llc.benefits', { returnObjects: true }) as string[],
      forWho: t('consulting.services.items.llc.forWho'),
    },
    {
      icon: 'ri-file-list-3-line',
      title: t('consulting.services.items.compliance.title'),
      color: '#00c2ff',
      image: 'https://readdy.ai/api/search-image?query=business%20licensing%20compliance%20documents%20checklist%20dark%20background%20neon%20blue%20glow%20professional%20entrepreneur%20regulatory%20requirements%20permits%20certificates%20official%20business%20setup&width=700&height=450&seq=lic1&orientation=landscape',
      desc: t('consulting.services.items.compliance.desc'),
      benefits: t('consulting.services.items.compliance.benefits', { returnObjects: true }) as string[],
      forWho: t('consulting.services.items.compliance.forWho'),
    },
    {
      icon: 'ri-organization-chart',
      title: t('consulting.services.items.strategy.title'),
      color: '#FF2DAA',
      image: 'https://readdy.ai/api/search-image?query=business%20strategy%20planning%20organizational%20chart%20whiteboard%20dark%20background%20neon%20pink%20glow%20entrepreneur%20consulting%20session%20growth%20roadmap%20professional%20advisor%20corporate%20structure&width=700&height=450&seq=bss1&orientation=landscape',
      desc: t('consulting.services.items.strategy.desc'),
      benefits: t('consulting.services.items.strategy.benefits', { returnObjects: true }) as string[],
      forWho: t('consulting.services.items.strategy.forWho'),
    },
    {
      icon: 'ri-team-line',
      title: t('consulting.services.items.network.title'),
      color: '#8c52ff',
      image: 'https://readdy.ai/api/search-image?query=professional%20business%20network%20support%20team%20collaboration%20dark%20background%20purple%20neon%20glow%20entrepreneur%20connections%20referrals%20attorneys%20accountants%20advisors%20trusted%20professionals&width=700&height=450&seq=pns1&orientation=landscape',
      desc: t('consulting.services.items.network.desc'),
      benefits: t('consulting.services.items.network.benefits', { returnObjects: true }) as string[],
      forWho: t('consulting.services.items.network.forWho'),
    },
  ];

  return (
    <section id="services" className="relative py-24 bg-[#030006] overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[radial-gradient(ellipse,rgba(140,82,255,0.06)_0%,transparent_70%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 bg-[#8c52ff]/10 border border-[#8c52ff]/30 rounded-full px-4 py-1.5 text-[#8c52ff] text-xs font-bold uppercase tracking-widest mb-5">
            <i className="ri-briefcase-4-line" />
            {t('consulting.services.hero.badge')}
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            {t('consulting.services.hero.titlePrefix')} <span className="bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] bg-clip-text text-transparent">{t('consulting.services.hero.titleHighlight')}</span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            {t('consulting.services.hero.subtitle')}
          </p>
        </div>

        {/* Service blocks */}
        <div className="space-y-20">
          {consultingServices.map((service, i) => (
            <ServiceBlock key={service.title} service={service} index={i} consultingUrl={consultingUrl} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ConsultingServices;
